import type { PositionDistributionV1 } from "@rekonas/domain";
import { authorizedFetch, parseResponse } from "./api";

export function parsePositionDistribution(value: unknown): PositionDistributionV1 {
  function object(v: unknown, keys: string[]): Record<string, unknown> {
    if (!v || typeof v !== "object" || Array.isArray(v) || Object.keys(v).length !== keys.length || keys.some((k) => !(k in v))) throw new TypeError("Invalid position distribution fields");
    return v as Record<string, unknown>;
  }
  function duration(v: unknown): number {
    if (typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 604800) throw new TypeError("Invalid position duration");
    return v;
  }
  const result = object(value, ["version", "algorithm", "convention", "recording_ref", "profile_sha256", "data_version", "duration_seconds", "sensors"]);
  if (result.version !== "1.0" || result.algorithm !== "position-duration-v1" || result.convention !== "gravity-position-v1-rx-neg90-projection-0.1-nearest-centre" ||
      typeof result.recording_ref !== "string" || !result.recording_ref || result.recording_ref.length > 120 ||
      typeof result.data_version !== "string" || !result.data_version || result.data_version.length > 120 ||
      typeof result.profile_sha256 !== "string" || !/^[0-9a-f]{64}$/.test(result.profile_sha256) || !duration(result.duration_seconds)) throw new TypeError("Invalid position identity");
  const sensors = object(result.sensors, ["head", "torso"]);
  for (const id of ["head", "torso"]) {
    const sensor = object(sensors[id], ["bins_seconds", "valid_seconds", "excluded"]);
    if (!Array.isArray(sensor.bins_seconds) || sensor.bins_seconds.length !== 360) throw new TypeError("Invalid position bins");
    const mass = sensor.bins_seconds.reduce<number>((sum, v: unknown) => sum + duration(v), 0);
    const excluded = object(sensor.excluded, ["missing_channels", "out_of_coverage", "acquisition_gap", "invalid", "near_upright"]);
    const excludedMass = Object.values(excluded).reduce<number>((sum, v) => sum + duration(v), 0);
    if (Math.abs(mass - duration(sensor.valid_seconds)) > 1e-6 || Math.abs(mass + excludedMass - duration(result.duration_seconds)) > 1e-6) throw new TypeError("Position durations do not reconcile");
  }
  return value as PositionDistributionV1;
}

export async function fetchPositionDistributionPath(path: string, signal?: AbortSignal): Promise<PositionDistributionV1> {
  return parsePositionDistribution(await parseResponse<unknown>(await authorizedFetch(path, { signal })));
}
